import React, { useState } from 'react';
import { Ticket, OrderApi } from '@/lib/OrderApi';
import { revealValidationSummary } from '@/components/customer-details-form/validationSummaryReveal';
import { CustomerDetailsForm } from '@/components/customer-details-form/CustomerDetailsForm';
import { OrderedList, FormComponent } from '@/lib/TicketApi';
import * as m from "@/paraglide/messages.js"

interface AmendTicketAnswersProps {
  orderId: string;
  ticket: Ticket;
  onSuccess: () => void;
}

const AmendTicketAnswers: React.FC<AmendTicketAnswersProps> = ({ orderId, ticket, onSuccess }) => {
  const personalisationUpdate = ticket.requiresPersonalisationUpdate ? ticket.personalisationUpdate : undefined;

  const [formState, setFormState] = useState<Record<string, object[] | string[]>>(personalisationUpdate?.answers ?? {});
  const [formValid, setFormValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  if (!personalisationUpdate) {
    return null;
  }

  const submit = async () => {
    if (!formValid) return;
    setLoading(true);
    setErrors({});
    try {
      const api = new OrderApi(import.meta.env.VITE_API_URL);
      const result = await api.amendTicketAnswers(orderId, ticket.ticketId, formState);
      if (!result.success) {
        setErrors(result.errors || {});
        setLoading(false);
        return;
      }
      onSuccess();
    } catch (error) {
      // Without this the request failing outright (offline, 500) left the visitor staring at an
      // unchanged form with no indication anything had happened at all.
      console.error(error);
      setErrors({ general: [m.form_something_went_wrong()] });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-center text-sm opacity-70">
        {m.ticket_amend_personalisation_intro()}
      </p>
      <CustomerDetailsForm
        form={personalisationUpdate.form as OrderedList<FormComponent>}
        answers={formState}
        errors={errors}
        setAnswers={(answers) => setFormState(answers as Record<string, object[] | string[]>)}
        setFormValid={setFormValid}
      />
      {/* The button stops taking pointer events once it is disabled, so the wrapper is what hears
          the visitor reach for it — hovering, or tapping on a phone, where there is no hover. */}
      <div onMouseEnter={revealValidationSummary} onClick={revealValidationSummary}>
        <button
          className="bg-[var(--shop-ctaButtonColor)] text-[var(--shop-ctaButtonTextColor)] p-2 rounded-md text-center block w-full hover:opacity-90 transition-all duration-300 hover:shadow-md disabled:opacity-50 disabled:pointer-events-none"
          disabled={!formValid || loading}
          onClick={submit}
        >
          {loading
            ? m.ticket_amend_personalisation_saving()
            : m.ticket_amend_personalisation_submit()}
        </button>
      </div>
    </div>
  );
};

export default AmendTicketAnswers;
